import { useState, useMemo, useCallback, type ChangeEvent, type MouseEvent } from 'react'
import { useNavigate } from '@tanstack/react-router'
import {
  Table,
  Badge,
  Button,
  Modal,
  TextInput,
  Stack,
  Group,
  Checkbox,
  ActionIcon,
  Text,
  Loader,
  Paper,
  Tooltip,
  Select,
  Pagination,
  Switch,
} from '@mantine/core'
import { useClipboard } from '@mantine/hooks'
import { notifications } from '@mantine/notifications'
import {
  IconPlus,
  IconTrash,
  IconSearch,
  IconArrowUp,
  IconArrowDown,
  IconCopy,
  IconExternalLink,
  IconDownload,
  IconUpload,
} from '@tabler/icons-react'
import { useGuests } from '../../hooks/useGuests'
import { supabase } from '../../lib/supabase'
import { formatDate } from '../../lib/utils'
import type { Guest } from '../../lib/types'
import styles from './GuestTable.module.css'
import * as XLSX from 'xlsx'

type SortField =
  | 'name'
  | 'pin'
  | 'contact'
  | 'status'
  | 'adults'
  | 'children'
  | 'invite_sent'
  | 'last_sent'
  | 'last_updated'

interface GuestTableProps {
  page: number
  pageSize: number | 'all'
  sortField: string
  sortDirection: 'asc' | 'desc'
  nameFilter: string
  statusFilter: string
  onStateChange: (updates: {
    page?: number
    pageSize?: number | 'all'
    sortField?: string
    sortDirection?: 'asc' | 'desc'
    nameFilter?: string
    statusFilter?: string
  }) => void
}

interface GuestTableRow {
  key: string
  guest: Guest
  displayName: string
  adults: number | null
  childrenDisplay: string | number
  isPlusOne: boolean
}

function formatChildrenList(guest: Guest): string {
  const children = guest.additional_guests?.filter((ag) => ag.type === 'child') ?? []
  if (children.length === 0) return '-'
  return children
    .map((child) => (child.age != null ? `${child.name} (${child.age})` : child.name))
    .join(', ')
}

function expandGuestToRows(guest: Guest): GuestTableRow[] {
  const plusOnes =
    guest.additional_guests
      ?.filter((ag) => ag.type === 'plus_one')
      .sort((a, b) => a.name.localeCompare(b.name)) ?? []
  const rows: GuestTableRow[] = [
    {
      key: `${guest.id}-primary`,
      guest,
      displayName: guest.primary_name,
      adults: null,
      childrenDisplay: formatChildrenList(guest),
      isPlusOne: false,
    },
  ]

  for (const plusOne of plusOnes) {
    rows.push({
      key: `${guest.id}-plus-${plusOne.id}`,
      guest,
      displayName: `${plusOne.name} (${guest.primary_name})`,
      adults: null,
      childrenDisplay: '-',
      isPlusOne: true,
    })
  }

  return rows
}

function guestToRow(guest: Guest): GuestTableRow {
  const { adults, children } = countAdultsAndChildren(guest)
  return {
    key: guest.id,
    guest,
    displayName: guest.primary_name,
    adults,
    childrenDisplay: children,
    isPlusOne: false,
  }
}

function countAdultsAndChildren(guest: Guest) {
  let adults = 1
  let children = 0

  if (guest.additional_guests) {
    guest.additional_guests.forEach((ag) => {
      if (ag.type === 'plus_one') {
        adults++
      } else if (ag.type === 'child') {
        children++
      }
    })
  }

  return { adults, children }
}

function guestMatchesNameFilter(guest: Guest, filter: string): boolean {
  if (guest.primary_name.toLowerCase().includes(filter)) return true
  return (
    guest.additional_guests?.some(
      (ag) => ag.type === 'plus_one' && ag.name.toLowerCase().includes(filter)
    ) ?? false
  )
}

export function GuestTable({
  page,
  pageSize,
  sortField: propSortField,
  sortDirection: propSortDirection,
  nameFilter: propNameFilter,
  statusFilter: propStatusFilter,
  onStateChange,
}: GuestTableProps) {
  const navigate = useNavigate()
  const { guests, loading, addGuest, deleteGuest, updateGuest, refetch } = useGuests()
  const clipboard = useClipboard({ timeout: 2000 })

  const [addModalOpen, setAddModalOpen] = useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [inviteModalOpen, setInviteModalOpen] = useState(false)
  const [importDeleteModalOpen, setImportDeleteModalOpen] = useState(false)
  const [selectedGuest, setSelectedGuest] = useState<Guest | null>(null)

  const [newGuestName, setNewGuestName] = useState('')
  const [newGuestContact, setNewGuestContact] = useState('')
  const [addingGuest, setAddingGuest] = useState(false)
  const [deletingGuest, setDeletingGuest] = useState(false)
  const [importing, setImporting] = useState(false)
  const [guestsToDelete, setGuestsToDelete] = useState<Guest[]>([])
  const [selectedGuestsToDelete, setSelectedGuestsToDelete] = useState<Set<string>>(new Set())
  const [importStats, setImportStats] = useState<{ created: number; updated: number } | null>(null)

  // Multi-select for bulk actions
  const [selectedGuests, setSelectedGuests] = useState<Set<string>>(new Set())
  const [bulkStatusModalOpen, setBulkStatusModalOpen] = useState(false)
  const [bulkNewStatus, setBulkNewStatus] = useState<'coming' | 'not_coming' | 'unknown' | null>(null)
  const [bulkUpdating, setBulkUpdating] = useState(false)
  const [showAdultsIndividually, setShowAdultsIndividually] = useState(false)

  // Use props for state
  const sortField = propSortField as SortField
  const sortDirection = propSortDirection
  const nameFilter = propNameFilter
  const statusFilter = propStatusFilter

  const handleAddGuest = async () => {
    if (!newGuestName.trim()) return

    setAddingGuest(true)
    try {
      // Generate unique PIN using database function
      const { data: pinData, error: pinError } = await supabase.rpc('generate_unique_pin')

      if (pinError || !pinData) {
        console.error('Error generating PIN:', pinError)
        return
      }

      const result = await addGuest({
        pin: pinData,
        primary_name: newGuestName.trim(),
        contact_info: newGuestContact.trim() || null,
        rsvp_status: 'unknown',
        invite_sent: false,
      })

      if (result) {
        setNewGuestName('')
        setNewGuestContact('')
        setAddModalOpen(false)
      }
    } catch (err) {
      console.error('Error adding guest:', err)
    } finally {
      setAddingGuest(false)
    }
  }

  const handleDeleteGuest = async () => {
    if (!selectedGuest) return

    setDeletingGuest(true)
    try {
      const success = await deleteGuest(selectedGuest.id)
      if (success) {
        notifications.show({
          title: 'Gast gelöscht',
          message: `${selectedGuest.primary_name} wurde entfernt.`,
          color: 'green',
        })
        setDeleteModalOpen(false)
        setSelectedGuest(null)
      } else {
        notifications.show({
          title: 'Löschen fehlgeschlagen',
          message: 'Der Gast konnte nicht gelöscht werden.',
          color: 'red',
        })
      }
    } finally {
      setDeletingGuest(false)
    }
  }

  const handleInviteSentToggle = async (guest: Guest) => {
    setSelectedGuest(guest)
    setInviteModalOpen(true)
  }

  const confirmInviteSent = async (newValue: boolean) => {
    if (!selectedGuest) return

    await updateGuest(selectedGuest.id, {
      invite_sent: newValue,
      last_sent_date: newValue ? new Date().toISOString() : null,
    })

    setInviteModalOpen(false)
    setSelectedGuest(null)
  }

  const compareRows = useCallback(
    (a: GuestTableRow, b: GuestTableRow) => {
      let compareValue = 0

      switch (sortField) {
        case 'name':
          compareValue = a.displayName.localeCompare(b.displayName)
          break
        case 'pin':
          compareValue = a.guest.pin.localeCompare(b.guest.pin)
          break
        case 'contact':
          compareValue = (a.guest.contact_info || '').localeCompare(b.guest.contact_info || '')
          break
        case 'status':
          compareValue = a.guest.rsvp_status.localeCompare(b.guest.rsvp_status)
          break
        case 'adults': {
          const aAdults = a.adults ?? countAdultsAndChildren(a.guest).adults
          const bAdults = b.adults ?? countAdultsAndChildren(b.guest).adults
          compareValue = aAdults - bAdults
          break
        }
        case 'children': {
          const aChildren =
            typeof a.childrenDisplay === 'number'
              ? a.childrenDisplay
              : countAdultsAndChildren(a.guest).children
          const bChildren =
            typeof b.childrenDisplay === 'number'
              ? b.childrenDisplay
              : countAdultsAndChildren(b.guest).children
          compareValue = aChildren - bChildren
          break
        }
        case 'invite_sent':
          compareValue = (a.guest.invite_sent ? 1 : 0) - (b.guest.invite_sent ? 1 : 0)
          break
        case 'last_sent':
          compareValue = (a.guest.last_sent_date || '').localeCompare(b.guest.last_sent_date || '')
          break
        case 'last_updated':
          compareValue = (a.guest.last_updated_date || '').localeCompare(
            b.guest.last_updated_date || ''
          )
          break
      }

      return sortDirection === 'asc' ? compareValue : -compareValue
    },
    [sortField, sortDirection]
  )

  const { tableRows, totalPages, totalCount, filteredTotals } = useMemo(() => {
    let filteredGuests = [...guests]

    if (nameFilter.trim()) {
      const filter = nameFilter.toLowerCase()
      filteredGuests = filteredGuests.filter((guest) => guestMatchesNameFilter(guest, filter))
    }

    if (statusFilter) {
      filteredGuests = filteredGuests.filter((guest) => guest.rsvp_status === statusFilter)
    }

    let adults = 0
    let totalPeople = 0
    for (const guest of filteredGuests) {
      const { adults: guestAdults, children } = countAdultsAndChildren(guest)
      adults += guestAdults
      totalPeople += guestAdults + children
    }

    let rows: GuestTableRow[]

    if (showAdultsIndividually) {
      filteredGuests.sort((a, b) => a.primary_name.localeCompare(b.primary_name, 'de'))
      rows = filteredGuests.flatMap(expandGuestToRows)
    } else {
      rows = filteredGuests.map(guestToRow)
      rows = [...rows].sort(compareRows)
    }

    const totalCount = rows.length

    if (pageSize === 'all') {
      return {
        tableRows: rows,
        totalPages: 1,
        totalCount,
        filteredTotals: { adults, totalPeople },
      }
    }

    const totalPages = Math.ceil(totalCount / pageSize)
    const startIndex = (page - 1) * pageSize
    const endIndex = startIndex + pageSize

    return {
      tableRows: rows.slice(startIndex, endIndex),
      totalPages,
      totalCount,
      filteredTotals: { adults, totalPeople },
    }
  }, [
    guests,
    nameFilter,
    statusFilter,
    page,
    pageSize,
    showAdultsIndividually,
    compareRows,
  ])

  const visibleGuestIds = useMemo(
    () => [...new Set(tableRows.map((row) => row.guest.id))],
    [tableRows]
  )

  const handleSort = (field: SortField) => {
    if (showAdultsIndividually) return

    if (sortField === field) {
      // Toggle direction if same field
      onStateChange({ sortDirection: sortDirection === 'asc' ? 'desc' : 'asc', page: 1 })
    } else {
      // Set new field with ascending direction
      onStateChange({ sortField: field, sortDirection: 'asc', page: 1 })
    }
  }

  const SortIcon = ({ field }: { field: SortField }) => {
    if (showAdultsIndividually || sortField !== field) return null
    return sortDirection === 'asc' ? <IconArrowUp size={14} /> : <IconArrowDown size={14} />
  }

  const sortableHeaderClass = showAdultsIndividually ? undefined : styles.sortableHeader

  const getRowNumber = (index: number) => {
    if (pageSize === 'all') return index + 1
    return (page - 1) * pageSize + index + 1
  }

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      coming: { color: 'green', label: 'Kommt' },
      not_coming: { color: 'red', label: 'Kommt nicht' },
      unknown: { color: 'gray', label: 'Unbekannt' },
    }

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.unknown
    return (
      <Badge
        color={config.color}
        variant="filled"
        size="sm"
        className={styles.statusBadge}
        style={{
          whiteSpace: 'nowrap',
          overflow: 'visible',
          textOverflow: 'clip',
          maxWidth: 'none',
        }}
      >
        {config.label}
      </Badge>
    )
  }

  const handleCopyPin = (pin: string, e: MouseEvent) => {
    e.stopPropagation() // Prevent row click navigation
    clipboard.copy(pin)
    notifications.show({
      title: 'PIN kopiert',
      message: 'Die PIN wurde in die Zwischenablage kopiert.',
      color: 'green',
      autoClose: 2000,
    })
  }

  // Helper function to parse CSV line (handles quoted fields)
  const parseCSVLine = (line: string): string[] => {
    const result: string[] = []
    let current = ''
    let inQuotes = false

    for (let i = 0; i < line.length; i++) {
      const char = line[i]
      const nextChar = line[i + 1]

      if (char === '"') {
        if (inQuotes && nextChar === '"') {
          // Escaped quote
          current += '"'
          i++ // Skip next quote
        } else {
          // Toggle quote state
          inQuotes = !inQuotes
        }
      } else if (char === ',' && !inQuotes) {
        // Field separator
        result.push(current)
        current = ''
      } else {
        current += char
      }
    }

    // Add last field
    result.push(current)
    return result
  }

  const handleImportCSV = async (file: File) => {
    try {
      setImporting(true)

      // Helper function to check if a value should be ignored
      const shouldIgnoreValue = (value: string): boolean => {
        const normalized = value.trim().toLowerCase()
        return normalized === 'mann' || normalized === 'frau' || normalized === 'kind'
      }

      // Read file
      const text = await file.text()
      const lines = text.split('\n').filter((line) => line.trim())

      if (lines.length < 5) {
        notifications.show({
          title: 'Import fehlgeschlagen',
          message:
            'Die CSV-Datei ist leer oder ungültig. Mindestens 4 Zeilen erforderlich (3 Header-Zeilen + Daten).',
          color: 'red',
          autoClose: 3000,
        })
        return
      }

      // Parse header (row 3, index 2) - should be "Einladen?,Gast,+1,Kind1,Kind2,Kind3,Kind4,Kind5,Kind6"
      const headerLine = lines[2]
      if (!headerLine) {
        notifications.show({
          title: 'Import fehlgeschlagen',
          message: 'Die CSV-Datei ist leer oder ungültig.',
          color: 'red',
          autoClose: 3000,
        })
        return
      }
      const header = parseCSVLine(headerLine)
      if (!header || header[1] !== 'Gast' || header[2] !== '+1') {
        notifications.show({
          title: 'Import fehlgeschlagen',
          message: 'Ungültiges CSV-Format. Erwartete Spalten in Zeile 3: Einladen?,Gast,+1,Kind1-6',
          color: 'red',
          autoClose: 3000,
        })
        return
      }

      // Parse data rows (skip first 3 rows, start at row 4, index 3)
      const importData: Array<{
        primaryName: string
        plusOne: string
        children: string[]
      }> = []

      const importedNames = new Set<string>()
      const namesToDelete = new Set<string>() // Track guests marked with "NEIN"

      for (let i = 3; i < lines.length; i++) {
        const line = lines[i]
        if (!line) continue
        const row = parseCSVLine(line)
        if (!row || row.length < 2) continue

        // First column indicates if row should be imported ("JA" or "NEIN")
        const shouldImport = (row[0] || '').trim().toUpperCase() === 'JA'
        const shouldDelete = (row[0] || '').trim().toUpperCase() === 'NEIN'

        // Get primary name from column 1 (index 1)
        const primaryNameRaw = (row[1] || '').trim()
        if (!primaryNameRaw) continue

        const primaryNameNormalized = primaryNameRaw.toLowerCase().trim()

        if (shouldImport) {
          // Import this row
          const primaryName = primaryNameRaw
          let plusOne = (row[2] || '').trim() // +1 is in column 2 (index 2)
          // Filter out +1 if it's "Mann" or "Frau"
          if (shouldIgnoreValue(plusOne)) {
            plusOne = ''
          }
          // Filter out children that are "Kind" - children are in columns 3-8 (indices 3-8)
          const children = (row.slice(3, 9) || [])
            .map((c) => c.trim())
            .filter((c) => c && !shouldIgnoreValue(c))

          importData.push({ primaryName, plusOne, children })
          // Store normalized name for exact matching
          importedNames.add(primaryNameNormalized)
        } else if (shouldDelete) {
          // Track this guest for potential deletion
          namesToDelete.add(primaryNameNormalized)
        }
      }

      // Debug: log imported names
      console.log('Imported names (normalized):', Array.from(importedNames))

      if (importData.length === 0) {
        notifications.show({
          title: 'Import fehlgeschlagen',
          message: 'Keine gültigen Daten in der CSV-Datei gefunden.',
          color: 'red',
          autoClose: 3000,
        })
        return
      }

      // Fetch all guests from database to ensure we have complete list for matching
      const { data: allGuestsForMatching, error: fetchError } = await supabase
        .from('guests')
        .select('*')
        .order('created_date', { ascending: false })

      if (fetchError) {
        console.error('Error fetching guests for matching:', fetchError)
        notifications.show({
          title: 'Import fehlgeschlagen',
          message: 'Fehler beim Abrufen der Gästeliste.',
          color: 'red',
          autoClose: 3000,
        })
        return
      }

      const allGuestsList = (allGuestsForMatching || []) as Guest[]

      // Process imports
      let updatedCount = 0
      let createdCount = 0

      for (const data of importData) {
        // Find existing guest by primary name (case-insensitive match on primary_name)
        const searchName = data.primaryName.toLowerCase().trim()
        const existingGuest = allGuestsList.find(
          (g) => g.primary_name.toLowerCase().trim() === searchName
        )

        if (existingGuest) {
          // Update existing guest: delete all additional guests and add new ones
          const { error: deleteError } = await supabase
            .from('additional_guests')
            .delete()
            .eq('guest_id', existingGuest.id)

          if (deleteError) {
            console.error('Error deleting additional guests:', deleteError)
            continue
          }

          // Insert new additional guests
          // Filter out +1 if it's "Mann" or "Frau", and children that are "Kind"
          const additionalGuestsToInsert: Array<{
            guest_id: string
            type: string
            name: string
            age: number | null
            added_by: string
          }> = []

          if (data.plusOne && !shouldIgnoreValue(data.plusOne)) {
            additionalGuestsToInsert.push({
              guest_id: existingGuest.id,
              type: 'plus_one',
              name: data.plusOne,
              age: null,
              added_by: 'admin',
            })
          }

          data.children.forEach((childName) => {
            if (!shouldIgnoreValue(childName)) {
              additionalGuestsToInsert.push({
                guest_id: existingGuest.id,
                type: 'child',
                name: childName,
                age: null, // Age is optional (N/A) when importing from CSV
                added_by: 'admin',
              })
            }
          })

          if (additionalGuestsToInsert.length > 0) {
            const { error: insertError } = await supabase
              .from('additional_guests')
              .insert(additionalGuestsToInsert)

            if (insertError) {
              console.error('Error inserting additional guests:', insertError)
            } else {
              updatedCount++
            }
          } else {
            updatedCount++
          }
        } else {
          // Create new guest
          const { data: pinData, error: pinError } = await supabase.rpc('generate_unique_pin')

          if (pinError || !pinData) {
            console.error('Error generating PIN:', pinError)
            continue
          }

          const { data: newGuest, error: insertError } = await supabase
            .from('guests')
            .insert({
              pin: pinData,
              primary_name: data.primaryName,
              contact_info: null,
              rsvp_status: 'unknown',
              invite_sent: false,
              created_by: 'admin',
            })
            .select()
            .single()

          if (insertError || !newGuest) {
            console.error('Error creating guest:', insertError)
            continue
          }

          // Add additional guests
          // Filter out +1 if it's "Mann" or "Frau", and children that are "Kind"
          const additionalGuestsToInsert: Array<{
            guest_id: string
            type: string
            name: string
            age: number | null
            added_by: string
          }> = []

          if (data.plusOne && !shouldIgnoreValue(data.plusOne)) {
            additionalGuestsToInsert.push({
              guest_id: newGuest.id,
              type: 'plus_one',
              name: data.plusOne,
              age: null,
              added_by: 'admin',
            })
          }

          data.children.forEach((childName) => {
            if (!shouldIgnoreValue(childName)) {
              additionalGuestsToInsert.push({
                guest_id: newGuest.id,
                type: 'child',
                name: childName,
                age: null, // Age is optional (N/A) when importing from CSV
                added_by: 'admin',
              })
            }
          })

          if (additionalGuestsToInsert.length > 0) {
            const { error: agError } = await supabase
              .from('additional_guests')
              .insert(additionalGuestsToInsert)

            if (agError) {
              console.error('Error inserting additional guests:', agError)
            }
          }

          createdCount++
        }
      }

      // Refetch guests to get updated data
      await refetch()

      // Fetch all guests after import to find ones not in import
      const { data: allGuestsData } = await supabase
        .from('guests')
        .select('*')
        .order('created_date', { ascending: false })

      // Find guests to delete:
      // 1. Guests not in import (not in importedNames)
      // 2. Guests explicitly marked with "NEIN" in CSV (in namesToDelete)
      const guestsToDeleteSet = new Set<string>()

      const allGuestsListForDeletion = (allGuestsData || []) as Guest[]

      allGuestsListForDeletion.forEach((g: { primary_name: string }) => {
        const guestName = g.primary_name.toLowerCase().trim()
        const isInImport = importedNames.has(guestName)
        const isMarkedForDeletion = namesToDelete.has(guestName)

        if (!isInImport || isMarkedForDeletion) {
          guestsToDeleteSet.add(guestName)
        }
      })

      // Get full guest objects for deletion
      const guestsToDeleteList = allGuestsListForDeletion.filter((g: { primary_name: string }) => {
        const guestName = g.primary_name.toLowerCase().trim()
        return guestsToDeleteSet.has(guestName)
      }) as Guest[]

      // Debug: log all guest names for comparison
      console.log(
        'All guest names in DB:',
        allGuestsListForDeletion.map((g: { primary_name: string }) => ({
          original: g.primary_name,
          normalized: g.primary_name.toLowerCase().trim(),
        }))
      )
      console.log('Guests marked for deletion:', Array.from(namesToDelete))
      console.log('Guests not in import:', Array.from(guestsToDeleteSet))

      // Store import stats
      setImportStats({ created: createdCount, updated: updatedCount })

      if (guestsToDeleteList.length > 0) {
        // Show modal to select guests to delete
        // Ensure we have valid guest objects with IDs
        const validGuestsToDelete = guestsToDeleteList.filter((g) => g.id && g.primary_name)
        console.log(
          'Guests to delete:',
          validGuestsToDelete.map((g) => ({ id: g.id, name: g.primary_name }))
        )
        setGuestsToDelete(validGuestsToDelete)
        setSelectedGuestsToDelete(new Set(validGuestsToDelete.map((g) => g.id)))
        setImportDeleteModalOpen(true)
      } else {
        notifications.show({
          title: 'Import erfolgreich',
          message: `${createdCount} Gäste erstellt, ${updatedCount} Gäste aktualisiert.`,
          color: 'green',
          autoClose: 3000,
        })
        setImportStats(null)
      }
    } catch (error) {
      console.error('Error importing CSV:', error)
      notifications.show({
        title: 'Import fehlgeschlagen',
        message: 'Beim Importieren der CSV-Datei ist ein Fehler aufgetreten.',
        color: 'red',
        autoClose: 3000,
      })
    } finally {
      setImporting(false)
    }
  }

  const handleFileInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      handleImportCSV(file)
    }
    // Reset input so same file can be selected again
    e.target.value = ''
  }

  const handleDeleteSelectedGuests = async () => {
    const guestsToDeleteIds = Array.from(selectedGuestsToDelete)

    if (guestsToDeleteIds.length === 0) {
      setImportDeleteModalOpen(false)
      setGuestsToDelete([])
      setSelectedGuestsToDelete(new Set())
      return
    }

    setImporting(true)

    try {
      // Delete all selected guests and track results
      const deleteResults: Array<{ id: string; success: boolean; name: string }> = []

      for (const guestId of guestsToDeleteIds) {
        const guest = guestsToDelete.find((g) => g.id === guestId)
        const guestName = guest?.primary_name || guestId

        console.log(`Attempting to delete guest: ${guestName} (ID: ${guestId})`)

        try {
          // First delete additional_guests if they exist (foreign key constraint)
          const { error: deleteAdditionalError } = await supabase
            .from('additional_guests')
            .delete()
            .eq('guest_id', guestId)

          if (deleteAdditionalError) {
            console.error(
              `Error deleting additional guests for ${guestName}:`,
              deleteAdditionalError
            )
            // Continue anyway, might not have additional guests
          }

          // Then delete the guest
          const { error: deleteError } = await supabase.from('guests').delete().eq('id', guestId)

          if (deleteError) {
            console.error(`Error deleting guest ${guestName}:`, deleteError)
            deleteResults.push({ id: guestId, success: false, name: guestName })
          } else {
            deleteResults.push({ id: guestId, success: true, name: guestName })
            console.log(`Successfully deleted guest: ${guestName}`)
          }
        } catch (error) {
          console.error(`Exception deleting guest ${guestName}:`, error)
          deleteResults.push({ id: guestId, success: false, name: guestName })
        }
      }

      const successCount = deleteResults.filter((r) => r.success).length
      const failedCount = deleteResults.filter((r) => !r.success).length

      // Refetch to ensure UI is up to date
      await refetch()

      if (failedCount > 0) {
        const failedNames = deleteResults
          .filter((r) => !r.success)
          .map((r) => r.name)
          .join(', ')
        notifications.show({
          title: 'Löschung teilweise fehlgeschlagen',
          message: `${successCount} Gäste gelöscht, ${failedCount} Fehler: ${failedNames}`,
          color: 'orange',
          autoClose: 5000,
        })
      } else {
        // Show import stats if available
        const statsMessage = importStats
          ? `${importStats.created} Gäste erstellt, ${importStats.updated} Gäste aktualisiert. ${successCount} Gäste gelöscht.`
          : `${successCount} Gäste wurden gelöscht.`

        notifications.show({
          title: 'Import abgeschlossen',
          message: statsMessage,
          color: 'green',
          autoClose: 4000,
        })
      }

      setImportStats(null)
    } catch (error) {
      console.error('Error deleting guests:', error)
      notifications.show({
        title: 'Löschung fehlgeschlagen',
        message: 'Beim Löschen der Gäste ist ein Fehler aufgetreten.',
        color: 'red',
        autoClose: 3000,
      })
    } finally {
      setImporting(false)
      setImportDeleteModalOpen(false)
      setGuestsToDelete([])
      setSelectedGuestsToDelete(new Set())
    }
  }

  const handleToggleAllDelete = (checked: boolean) => {
    if (checked) {
      setSelectedGuestsToDelete(new Set(guestsToDelete.map((g) => g.id)))
    } else {
      setSelectedGuestsToDelete(new Set())
    }
  }

  const handleToggleGuestDelete = (guestId: string, checked: boolean) => {
    const newSet = new Set(selectedGuestsToDelete)
    if (checked) {
      newSet.add(guestId)
    } else {
      newSet.delete(guestId)
    }
    setSelectedGuestsToDelete(newSet)
  }

  // Bulk selection handlers
  const handleToggleSelectGuest = useCallback((guestId: string, checked: boolean, e: MouseEvent) => {
    e.stopPropagation()
    setSelectedGuests((prev) => {
      const next = new Set(prev)
      if (checked) {
        next.add(guestId)
      } else {
        next.delete(guestId)
      }
      return next
    })
  }, [])

  const handleToggleSelectAll = useCallback(
    (checked: boolean) => {
      if (checked) {
        setSelectedGuests(new Set(visibleGuestIds))
      } else {
        setSelectedGuests(new Set())
      }
    },
    [visibleGuestIds]
  )

  const visibleSelectedCount = useMemo(
    () => visibleGuestIds.filter((id) => selectedGuests.has(id)).length,
    [visibleGuestIds, selectedGuests]
  )

  const handleBulkStatusChange = async () => {
    if (!bulkNewStatus || selectedGuests.size === 0) return

    setBulkUpdating(true)
    try {
      let successCount = 0
      let failCount = 0

      for (const guestId of selectedGuests) {
        const success = await updateGuest(guestId, { rsvp_status: bulkNewStatus })
        if (success) {
          successCount++
        } else {
          failCount++
        }
      }

      if (failCount > 0) {
        notifications.show({
          title: 'Teilweise aktualisiert',
          message: `${successCount} Gäste aktualisiert, ${failCount} Fehler.`,
          color: 'orange',
          autoClose: 4000,
        })
      } else {
        const statusLabel =
          bulkNewStatus === 'coming' ? 'Kommt' : bulkNewStatus === 'not_coming' ? 'Kommt nicht' : 'Unbekannt'
        notifications.show({
          title: 'Status aktualisiert',
          message: `${successCount} Gäste auf "${statusLabel}" gesetzt.`,
          color: 'green',
          autoClose: 3000,
        })
      }

      setSelectedGuests(new Set())
    } catch (err) {
      console.error('Bulk update error:', err)
      notifications.show({
        title: 'Fehler',
        message: 'Beim Aktualisieren ist ein Fehler aufgetreten.',
        color: 'red',
        autoClose: 3000,
      })
    } finally {
      setBulkUpdating(false)
      setBulkStatusModalOpen(false)
      setBulkNewStatus(null)
    }
  }

  const handleExportCSV = () => {
    try {
      // Helper function to escape CSV fields (wrap in quotes if contains comma or quote)
      const escapeCSVField = (field: string): string => {
        if (field.includes(',') || field.includes('"') || field.includes('\n')) {
          // Escape quotes by doubling them and wrap in quotes
          return `"${field.replace(/"/g, '""')}"`
        }
        return field
      }

      // CSV header
      const header = 'Gast,+1,Kind1,Kind2,Kind3,Kind4,Kind5,Kind6'

      // Process all guests (not just filtered ones)
      const rows = guests.map((guest) => {
        const primaryName = escapeCSVField(guest.primary_name || '')
        let plusOne = ''
        const children: string[] = []

        // Extract plus_one and children from additional_guests
        if (guest.additional_guests && guest.additional_guests.length > 0) {
          guest.additional_guests.forEach((ag) => {
            if (ag.type === 'plus_one') {
              plusOne = escapeCSVField(ag.name || '')
            } else if (ag.type === 'child') {
              children.push(escapeCSVField(ag.name || ''))
            }
          })
        }

        // Limit to 6 children
        const childrenArray = children.slice(0, 6)
        // Pad with empty strings if less than 6
        while (childrenArray.length < 6) {
          childrenArray.push('')
        }

        // Build CSV row
        return [primaryName, plusOne, ...childrenArray].join(',')
      })

      // Combine header and rows
      const csvContent = [header, ...rows].join('\n')

      // Create blob and download
      const now = new Date()
      const dateStr = now.toISOString().split('T')[0] // YYYY-MM-DD
      const timeStr = now.toTimeString().split(' ')[0]?.replace(/:/g, '-') || '00-00-00' // HH-MM-SS
      const filename = `gaeste_export_${dateStr}_${timeStr}.csv`

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      link.setAttribute('download', filename)
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      notifications.show({
        title: 'Export erfolgreich',
        message: 'Die Gästeliste wurde als CSV exportiert.',
        color: 'green',
        autoClose: 2000,
      })
    } catch (error) {
      console.error('Error exporting CSV:', error)
      notifications.show({
        title: 'Export fehlgeschlagen',
        message: 'Beim Exportieren der CSV-Datei ist ein Fehler aufgetreten.',
        color: 'red',
        autoClose: 3000,
      })
    }
  }

  const handleExportKartenliebe = () => {
    try {
      // Prepare data for Excel export
      const data: Array<[string, string]> = []

      // Add header row
      data.push(['Text 1', 'Text 2'])

      // Process all guests (not just filtered ones)
      guests.forEach((guest) => {
        // Find plus_one in additional_guests
        let plusOneName = ''
        if (guest.additional_guests && guest.additional_guests.length > 0) {
          const plusOne = guest.additional_guests.find((ag) => ag.type === 'plus_one')
          if (plusOne) {
            plusOneName = plusOne.name || ''
          }
        }

        // Format text column: if no +1, just the name; if +1 exists, format with newlines
        let text1: string
        if (plusOneName) {
          text1 = `${guest.primary_name || ''}\n&\n${plusOneName}`
        } else {
          text1 = guest.primary_name || ''
        }

        // Add row: [Text 1, Text 2 (PIN)]
        data.push([text1, guest.pin || ''])
      })

      // Create workbook and worksheet
      const worksheet = XLSX.utils.aoa_to_sheet(data)
      const workbook = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Gäste')

      // Generate filename with timestamp
      const now = new Date()
      const dateStr = now.toISOString().split('T')[0] // YYYY-MM-DD
      const timeStr = now.toTimeString().split(' ')[0]?.replace(/:/g, '-') || '00-00-00' // HH-MM-SS
      const filename = `kartenliebe_export_${dateStr}_${timeStr}.xlsx`

      // Write file
      XLSX.writeFile(workbook, filename)

      notifications.show({
        title: 'Export erfolgreich',
        message: 'Die Kartenliebe-Export-Datei wurde erstellt.',
        color: 'green',
        autoClose: 2000,
      })
    } catch (error) {
      console.error('Error exporting Kartenliebe XLSX:', error)
      notifications.show({
        title: 'Export fehlgeschlagen',
        message: 'Beim Exportieren der Kartenliebe-Datei ist ein Fehler aufgetreten.',
        color: 'red',
        autoClose: 3000,
      })
    }
  }

  if (loading) {
    return (
      <Paper p="xl" className={styles.loadingContainer}>
        <Stack align="center" gap="md">
          <Loader size="lg" />
          <Text c="dimmed">Lädt...</Text>
        </Stack>
      </Paper>
    )
  }

  return (
    <div className={styles.container}>
      <Group justify="space-between" mb="lg">
        <Text size="xl" fw={600}>
          Gästeliste
        </Text>
        <Group gap="sm">
          <input
            type="file"
            accept=".csv"
            onChange={handleFileInputChange}
            style={{ display: 'none' }}
            id="csv-import-input"
          />
          <Button
            component="label"
            htmlFor="csv-import-input"
            leftSection={<IconUpload size={18} />}
            variant="outline"
            loading={importing}
            disabled={importing}
          >
            CSV importieren
          </Button>
          <Button
            leftSection={<IconDownload size={18} />}
            onClick={handleExportCSV}
            variant="outline"
          >
            CSV exportieren
          </Button>
          <Button
            leftSection={<IconDownload size={18} />}
            onClick={handleExportKartenliebe}
            variant="outline"
          >
            Kartenliebe export
          </Button>
          <Button
            leftSection={<IconExternalLink size={18} />}
            onClick={() => navigate({ to: '/admin/kartenliebe-daten' })}
            variant="outline"
          >
            Kartenliebe daten
          </Button>
          <Button
            leftSection={<IconPlus size={18} />}
            onClick={() => setAddModalOpen(true)}
            className={styles.addButton}
          >
            Gast hinzufügen
          </Button>
        </Group>
      </Group>

      {/* Search/Filter Inputs */}
      <Group mb="md" gap="md" justify="space-between" align="flex-end">
        <Group gap="md" style={{ flex: 1 }}>
          <TextInput
            placeholder="Nach Name suchen..."
            leftSection={<IconSearch size={16} />}
            value={nameFilter}
            onChange={(e) => {
              onStateChange({ nameFilter: e.currentTarget.value, page: 1 })
            }}
            className={styles.searchInput}
            style={{ flex: 1 }}
          />
          <Select
            placeholder="Status filtern"
            value={statusFilter || null}
            onChange={(value) => {
              onStateChange({ statusFilter: value || '', page: 1 })
            }}
            data={[
              { value: 'coming', label: 'Kommt' },
              { value: 'not_coming', label: 'Kommt nicht' },
              { value: 'unknown', label: 'Unbekannt' },
            ]}
            clearable
            style={{ width: 160 }}
          />
        </Group>
        <Switch
          label="Alle Erwachsene einzeln"
          checked={showAdultsIndividually}
          onChange={(e) => {
            setShowAdultsIndividually(e.currentTarget.checked)
            onStateChange({ page: 1 })
          }}
        />
      </Group>

      {/* Bulk Action Bar */}
      {selectedGuests.size > 0 && (
        <Paper p="sm" mb="md" className={styles.bulkActionBar}>
          <Group justify="space-between">
            <Text size="sm" fw={500}>
              {selectedGuests.size} {selectedGuests.size === 1 ? 'Gast' : 'Gäste'} ausgewählt
            </Text>
            <Group gap="sm">
              <Select
                placeholder="Status ändern..."
                value={bulkNewStatus}
                onChange={(value) => {
                  if (value) {
                    setBulkNewStatus(value as 'coming' | 'not_coming' | 'unknown')
                    setBulkStatusModalOpen(true)
                  }
                }}
                data={[
                  { value: 'coming', label: 'Kommt' },
                  { value: 'not_coming', label: 'Kommt nicht' },
                  { value: 'unknown', label: 'Unbekannt' },
                ]}
                size="sm"
                style={{ width: 180 }}
              />
              <Button
                variant="subtle"
                size="sm"
                onClick={() => setSelectedGuests(new Set())}
              >
                Auswahl aufheben
              </Button>
            </Group>
          </Group>
        </Paper>
      )}

      {guests.length === 0 ? (
        <Paper p="xl" className={styles.emptyState}>
          <Stack align="center" gap="md">
            <Text size="lg" c="dimmed">
              Keine Gäste verfügbar
            </Text>
            <Text size="sm" c="dimmed">
              Fügen Sie Ihren ersten Gast hinzu
            </Text>
          </Stack>
        </Paper>
      ) : (
        <Paper shadow="sm" className={styles.tableContainer}>
          <Table striped highlightOnHover className={styles.table}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th className={styles.rowNumberHeader}>#</Table.Th>
                <Table.Th style={{ width: 40 }}>
                  <Checkbox
                    checked={
                      visibleGuestIds.length > 0 && visibleSelectedCount === visibleGuestIds.length
                    }
                    indeterminate={
                      visibleSelectedCount > 0 && visibleSelectedCount < visibleGuestIds.length
                    }
                    onChange={(e) => handleToggleSelectAll(e.currentTarget.checked)}
                    aria-label="Alle auswählen"
                  />
                </Table.Th>
                <Table.Th className={sortableHeaderClass} onClick={() => handleSort('name')}>
                  <Group gap="xs" wrap="nowrap">
                    Name
                    <SortIcon field="name" />
                  </Group>
                </Table.Th>
                <Table.Th className={sortableHeaderClass} onClick={() => handleSort('pin')}>
                  <Group gap="xs" wrap="nowrap">
                    PIN
                    <SortIcon field="pin" />
                  </Group>
                </Table.Th>
                <Table.Th className={sortableHeaderClass} onClick={() => handleSort('status')}>
                  <Group gap="xs" wrap="nowrap">
                    Status
                    <SortIcon field="status" />
                  </Group>
                </Table.Th>
                {!showAdultsIndividually && (
                  <Table.Th className={sortableHeaderClass} onClick={() => handleSort('adults')}>
                    <Group gap="xs" wrap="nowrap">
                      Erwachsene
                      <SortIcon field="adults" />
                    </Group>
                  </Table.Th>
                )}
                <Table.Th className={sortableHeaderClass} onClick={() => handleSort('children')}>
                  <Group gap="xs" wrap="nowrap">
                    Kinder
                    <SortIcon field="children" />
                  </Group>
                </Table.Th>
                <Table.Th
                  className={sortableHeaderClass}
                  onClick={() => handleSort('invite_sent')}
                >
                  <Group gap="xs" wrap="nowrap">
                    Einladung gesendet?
                    <SortIcon field="invite_sent" />
                  </Group>
                </Table.Th>
                <Table.Th className={sortableHeaderClass} onClick={() => handleSort('last_sent')}>
                  <Group gap="xs" wrap="nowrap">
                    Zuletzt gesendet
                    <SortIcon field="last_sent" />
                  </Group>
                </Table.Th>
                <Table.Th
                  className={sortableHeaderClass}
                  onClick={() => handleSort('last_updated')}
                >
                  <Group gap="xs" wrap="nowrap">
                    Zuletzt aktualisiert
                    <SortIcon field="last_updated" />
                  </Group>
                </Table.Th>
                <Table.Th>Aktionen</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {tableRows.map((row, index) => {
                const guest = row.guest
                return (
                  <Table.Tr
                    key={row.key}
                    className={`${styles.tableRow} ${selectedGuests.has(guest.id) ? styles.selectedRow : ''} ${row.isPlusOne ? styles.plusOneRow : ''}`}
                  >
                    <Table.Td className={styles.rowNumberCell}>{getRowNumber(index)}</Table.Td>
                    <Table.Td>
                      <Checkbox
                        checked={selectedGuests.has(guest.id)}
                        onChange={() => {}}
                        onClick={(e) =>
                          handleToggleSelectGuest(
                            guest.id,
                            !selectedGuests.has(guest.id),
                            e as unknown as MouseEvent
                          )
                        }
                        aria-label={`${row.displayName} auswählen`}
                      />
                    </Table.Td>
                    <Table.Td
                      className={styles.nameCell}
                      onClick={() => navigate({ to: `/admin/guest/${guest.id}` })}
                    >
                      {row.displayName}
                    </Table.Td>
                    <Table.Td>
                      <div className={styles.badgeWrapper}>
                        <Tooltip label="Klicken zum Kopieren" withArrow>
                          <Badge
                            variant="light"
                            size="lg"
                            className={styles.pinBadge}
                            onClick={(e) => handleCopyPin(guest.pin, e)}
                            style={{
                              cursor: 'pointer',
                              whiteSpace: 'nowrap',
                              overflow: 'visible',
                              textOverflow: 'clip',
                              maxWidth: 'none',
                            }}
                          >
                            <Group gap={4} wrap="nowrap">
                              {guest.pin}
                              <IconCopy size={14} />
                            </Group>
                          </Badge>
                        </Tooltip>
                      </div>
                    </Table.Td>
                    <Table.Td>
                      <div className={styles.badgeWrapper}>{getStatusBadge(guest.rsvp_status)}</div>
                    </Table.Td>
                    {!showAdultsIndividually && <Table.Td>{row.adults}</Table.Td>}
                    <Table.Td>{row.childrenDisplay}</Table.Td>
                    <Table.Td>
                      <Checkbox
                        checked={guest.invite_sent}
                        onChange={(e) => {
                          e.stopPropagation()
                          handleInviteSentToggle(guest)
                        }}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </Table.Td>
                    <Table.Td>
                      {guest.last_sent_date
                        ? formatDate(new Date(guest.last_sent_date), 'en')
                        : '-'}
                    </Table.Td>
                    <Table.Td>
                      {guest.last_updated_date
                        ? formatDate(new Date(guest.last_updated_date), 'en')
                        : '-'}
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs" onClick={(e) => e.stopPropagation()}>
                        <Tooltip label="Gast-Ansicht öffnen" withArrow>
                          <ActionIcon
                            variant="subtle"
                            color="blue"
                            onClick={() => {
                              const guestUrl = `${window.location.origin}/guest/pin?pin=${guest.pin}`
                              window.open(guestUrl, '_blank', 'noopener,noreferrer')
                            }}
                          >
                            <IconExternalLink size={18} />
                          </ActionIcon>
                        </Tooltip>
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          onClick={(e) => {
                            e.stopPropagation()
                            setSelectedGuest(guest)
                            setDeleteModalOpen(true)
                          }}
                        >
                          <IconTrash size={18} />
                        </ActionIcon>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                )
              })}
            </Table.Tbody>
          </Table>
        </Paper>
      )}

      {guests.length > 0 && (
        <Group gap="lg" mt="sm" className={styles.tableSummary}>
          <Text size="sm" fw={500}>
            Erwachsene: {filteredTotals.adults}
          </Text>
          <Text size="sm" fw={500}>
            Erwachsene + Kinder: {filteredTotals.totalPeople}
          </Text>
        </Group>
      )}

      {/* Pagination and Page Size */}
      <Group justify="space-between" mt="md" align="center">
        <Text size="sm" c="dimmed">
          {pageSize !== 'all'
            ? showAdultsIndividually
              ? `Zeige ${tableRows.length} von ${totalCount} Einträgen`
              : `Zeige ${tableRows.length} von ${totalCount} Gästen`
            : showAdultsIndividually
              ? `${totalCount} Einträge`
              : `${totalCount} Gäste`}
        </Text>
        <Group gap="md">
          <Select
            value={pageSize === 'all' ? 'all' : String(pageSize)}
            onChange={(value) => {
              onStateChange({
                pageSize: value === 'all' ? 'all' : Number(value),
                page: 1,
              })
            }}
            data={[
              { value: '5', label: '5' },
              { value: '10', label: '10' },
              { value: '25', label: '25' },
              { value: '100', label: '100' },
              { value: 'all', label: 'Alle' },
            ]}
            style={{ width: 100 }}
          />
          {pageSize !== 'all' && totalPages > 1 && (
            <Pagination
              value={page}
              onChange={(newPage) => onStateChange({ page: newPage })}
              total={totalPages}
            />
          )}
        </Group>
      </Group>

      {/* Add Guest Modal */}
      <Modal
        opened={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        title="Gast hinzufügen"
        centered
      >
        <form
          onSubmit={(e) => {
            e.preventDefault()
            if (newGuestName.trim() && !addingGuest) {
              handleAddGuest()
            }
          }}
        >
          <Stack>
            <TextInput
              label="Name"
              placeholder="Name des Gastes"
              value={newGuestName}
              onChange={(e) => setNewGuestName(e.currentTarget.value)}
              required
              autoFocus
            />
            <TextInput
              label="Kontakt"
              placeholder="E-Mail oder Telefon (optional)"
              value={newGuestContact}
              onChange={(e) => setNewGuestContact(e.currentTarget.value)}
            />
            <Group justify="flex-end" mt="md">
              <Button variant="subtle" type="button" onClick={() => setAddModalOpen(false)}>
                Abbrechen
              </Button>
              <Button type="submit" loading={addingGuest} disabled={!newGuestName.trim()}>
                Hinzufügen
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        opened={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Gast löschen"
        centered
      >
        <Stack>
          <Text>
            Möchten Sie diesen Gast wirklich löschen?
            <br />
            <strong>{selectedGuest?.primary_name}</strong>
          </Text>
          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={() => setDeleteModalOpen(false)}>
              Abbrechen
            </Button>
            <Button color="red" onClick={handleDeleteGuest} loading={deletingGuest}>
              Löschen
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Invite Sent Confirmation Modal */}
      <Modal
        opened={inviteModalOpen}
        onClose={() => setInviteModalOpen(false)}
        title="Einladung gesendet?"
        centered
      >
        <Stack>
          <Text>
            {selectedGuest?.invite_sent
              ? 'Senden rückgängig machen?'
              : 'Einladung als gesendet markieren?'}
          </Text>
          <Group justify="flex-end" mt="md">
            <Button
              variant="subtle"
              onClick={() => {
                setInviteModalOpen(false)
                setSelectedGuest(null)
              }}
            >
              Nein
            </Button>
            <Button onClick={() => confirmInviteSent(!selectedGuest?.invite_sent)}>Ja</Button>
          </Group>
        </Stack>
      </Modal>

      {/* Bulk Status Change Confirmation Modal */}
      <Modal
        opened={bulkStatusModalOpen}
        onClose={() => {
          setBulkStatusModalOpen(false)
          setBulkNewStatus(null)
        }}
        title="RSVP-Status ändern"
        centered
      >
        <Stack>
          <Text>
            Möchten Sie den Status von{' '}
            <strong>
              {selectedGuests.size} {selectedGuests.size === 1 ? 'Gast' : 'Gästen'}
            </strong>{' '}
            auf{' '}
            <strong>
              {bulkNewStatus === 'coming'
                ? 'Kommt'
                : bulkNewStatus === 'not_coming'
                  ? 'Kommt nicht'
                  : 'Unbekannt'}
            </strong>{' '}
            ändern?
          </Text>
          <Group justify="flex-end" mt="md">
            <Button
              variant="subtle"
              onClick={() => {
                setBulkStatusModalOpen(false)
                setBulkNewStatus(null)
              }}
            >
              Abbrechen
            </Button>
            <Button onClick={handleBulkStatusChange} loading={bulkUpdating}>
              Status ändern
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Import Delete Selection Modal */}
      <Modal
        opened={importDeleteModalOpen}
        onClose={() => {
          setImportDeleteModalOpen(false)
          setGuestsToDelete([])
          setSelectedGuestsToDelete(new Set())
          if (importStats) {
            notifications.show({
              title: 'Import erfolgreich',
              message: `${importStats.created} Gäste erstellt, ${importStats.updated} Gäste aktualisiert.`,
              color: 'green',
              autoClose: 3000,
            })
            setImportStats(null)
          }
        }}
        title="Gäste löschen"
        centered
        size="lg"
      >
        <Stack>
          <Text>
            Die folgenden Gäste sind nicht in der importierten CSV-Datei enthalten. Wählen Sie aus,
            welche gelöscht werden sollen:
          </Text>

          {guestsToDelete.length > 0 && (
            <>
              <Group justify="space-between" mt="md">
                <Checkbox
                  label="Alle auswählen"
                  checked={selectedGuestsToDelete.size === guestsToDelete.length}
                  indeterminate={
                    selectedGuestsToDelete.size > 0 &&
                    selectedGuestsToDelete.size < guestsToDelete.length
                  }
                  onChange={(e) => handleToggleAllDelete(e.currentTarget.checked)}
                />
                <Text size="sm" c="dimmed">
                  {selectedGuestsToDelete.size} von {guestsToDelete.length} ausgewählt
                </Text>
              </Group>

              <Paper withBorder p="md" style={{ maxHeight: 400, overflow: 'auto' }}>
                <Table>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th style={{ width: 50 }}></Table.Th>
                      <Table.Th>Name</Table.Th>
                      <Table.Th>PIN</Table.Th>
                      <Table.Th>Status</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {guestsToDelete.map((guest) => (
                      <Table.Tr key={guest.id}>
                        <Table.Td>
                          <Checkbox
                            checked={selectedGuestsToDelete.has(guest.id)}
                            onChange={(e) =>
                              handleToggleGuestDelete(guest.id, e.currentTarget.checked)
                            }
                          />
                        </Table.Td>
                        <Table.Td>{guest.primary_name}</Table.Td>
                        <Table.Td>
                          <Badge variant="light" size="sm">
                            {guest.pin}
                          </Badge>
                        </Table.Td>
                        <Table.Td>{getStatusBadge(guest.rsvp_status)}</Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Paper>
            </>
          )}

          <Group justify="flex-end" mt="md">
            <Button
              variant="subtle"
              onClick={() => {
                setImportDeleteModalOpen(false)
                setGuestsToDelete([])
                setSelectedGuestsToDelete(new Set())
                if (importStats) {
                  notifications.show({
                    title: 'Import erfolgreich',
                    message: `${importStats.created} Gäste erstellt, ${importStats.updated} Gäste aktualisiert.`,
                    color: 'green',
                    autoClose: 3000,
                  })
                  setImportStats(null)
                }
              }}
            >
              Abbrechen
            </Button>
            <Button
              color="red"
              onClick={handleDeleteSelectedGuests}
              loading={importing}
              disabled={selectedGuestsToDelete.size === 0}
            >
              Ausgewählte löschen ({selectedGuestsToDelete.size})
            </Button>
          </Group>
        </Stack>
      </Modal>
    </div>
  )
}
